import { Dispatch as ReactDispatch, useState, useEffect, ChangeEvent, SetStateAction, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useHistory } from 'react-router';

import { Button } from 'UIPackage/MainModule/components/Button';
import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { num2str } from 'CorePackage/MainModule/common/num2str';
import { ROUTE_DECISION_NEW } from 'CorePackage/MainModule/routes/routes';
import { getUserRoleIsSupervisor, getUserProfileIsTest } from 'AuthPackage/MainModule/selectors/authSelectors';
import { FilterCheckbox } from 'UIPackage/MainModule/components/FilterCheckbox';
import { getStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { decisionListFilterSetSwitcher } from 'DecisionListPackage/DecisionListFiltersModule/actions/decisionListFilterActions';
import { DecisionListFiltersModal } from 'DecisionListPackage/DecisionListFiltersModule/components/DecisionListFiltersModal';

import styles from './DecisionListPanel.module.scss';
import { getDecisionListData, getDecisionListTotalCount } from '../selectors/decisionListSelectors';
import { IDecisionListItem } from '../interfaces/IDecisionListItem';
import {
  decisionListGetData,
  decisionListRemoveDecisions,
  decisionListSetSearchValue,
} from '../actions/decisionListActions';
import { KnmErknmStatusCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';

interface IDecisionListPanelProps {
  itemsCheck: TableCheckItems;
  searchInputValue: string;
  setSearchInputValue: ReactDispatch<SetStateAction<string>>;
}

interface IDecisionListPanelStoreProps {
  data: IDecisionListItem[];
  decisionsCount: number;
  isSuperVisor: boolean;
  isTestUser: boolean;
}

const DecisionListPanelWithoutStore = ({
  itemsCheck,
  searchInputValue,
  setSearchInputValue,
  data,
  isSuperVisor,
  isTestUser,
  decisionsCount,
}: IDecisionListPanelProps & IDecisionListPanelStoreProps) => {
  const history = useHistory();

  const isFilterCheckboxActive = !!getStorageItem(StorageNamesEnum.DECISION_FILTER_ON);

  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);
  const [isDeleteButtonEnabled, setIsDeleteButtonEnabled] = useState(false);
  const [isSearchInputValid, setIsSearchInputValid] = useState(false);
  const [searchInputError, setSearchInputError] = useState('');
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const keysCheckedItems = Object.keys(itemsCheck).filter(id => itemsCheck[id]);

  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    const checkedItems = data.filter(item => itemsCheck[item.id]);

    setIsDeleteButtonEnabled(
      checkedItems.length > 0 &&
        !checkedItems.find(x => x.statusCode !== KnmErknmStatusCodesEnum.FILL_IN_PROCESS) &&
        (!isTestUser ? true : checkedItems.every(item => item.isTest)),
    );
  }, [data, itemsCheck, isTestUser]);

  const onAddDecisionClick = useCallback(() => {
    history.push(ROUTE_DECISION_NEW);
  }, [history]);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      setSearchInputValue(value);

      const isValid = value.trim().length !== 0 || isSearched;

      setIsSearchInputValid(isValid);
    },
    [setSearchInputValue, isSearched],
  );

  const handleSearchInputSubmit = useCallback(() => {
    if (!isSearchInputValid) {
      setSearchInputError('Неверный формат строки');
    } else {
      setSearchInputError('');
    }
    if (searchInputValue.length) {
      setIsSearched(true);
    } else {
      setIsSearchInputValid(false);
      setIsSearched(false);
    }
    dispatch(decisionListSetSearchValue(searchInputValue));
    dispatch(decisionListGetData());
  }, [dispatch, isSearchInputValid, searchInputValue]);

  const handleOnBlur = useCallback(() => {
    !isSearchInputValid ? setSearchInputError('Неверный формат строки') : setSearchInputError('');
  }, [isSearchInputValid]);

  const handleDeleteClick = useCallback(() => {
    const ids = keysCheckedItems.map(key => Number(key));

    dispatch(decisionListRemoveDecisions(ids));
  }, [dispatch, keysCheckedItems]);

  const deletingQuantityText = num2str(keysCheckedItems.length, ['решение', 'решения', 'решений']);

  const handleDeleteModalOpen = useCallback(() => setIsConfirmModalVisible(true), []);

  const handleDeleteModalClose = useCallback(() => setIsConfirmModalVisible(false), []);

  const handleFilterCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(decisionListFilterSetSwitcher(e.target.checked));
      dispatch(decisionListGetData());
    },
    [dispatch],
  );

  const handleFilterButtonClick = useCallback(() => {
    setIsFiltersModalVisible(true);
  }, []);

  const closeFiltersModal = useCallback(() => setIsFiltersModalVisible(false), []);

  const filterButtonAttributes = {
    ...(isFilterCheckboxActive && { 'data-active': true }),
  };

  return (
    <div className="shared-panel-container">
      <div className={styles.Actions}>
        {isSuperVisor && (
          <>
            <Button
              startIcon={styles.AddIcon}
              text="Добавить"
              id="addButton"
              color="primary"
              onClick={onAddDecisionClick}
            />
            <Button
              className={styles.ButtonDelete}
              startIcon={styles.DeleteIcon}
              text="Удалить"
              id="deleteButton"
              onClick={handleDeleteModalOpen}
              disabled={!isDeleteButtonEnabled}
            />
          </>
        )}

        <SearchInput
          className={styles.SearchInput}
          value={searchInputValue}
          name="searchString"
          placeholder="Поиск решений"
          buttonDisabled={!isSearchInputValid}
          error={searchInputError}
          onChange={handleSearchInputChange}
          onSubmit={handleSearchInputSubmit}
          onBlur={handleOnBlur}
        />
        <Tooltip
          placement="right"
          content={
            <div className={styles.TooltipContent}>
              Доступен поиск решений по следующим критериям:
              <ul>
                <li>Учетный номер решения</li>
              </ul>
            </div>
          }
        >
          <span className={styles.TooltipIcon} />
        </Tooltip>
      </div>
      <div className={styles.Filters}>
        <p className={styles.CurrentFilter}>
          {'Мероприятия моей организации'}&nbsp;
          <small className={styles.CurrentFilterNumbers}>({decisionsCount})</small>
        </p>
        <FilterCheckbox
          className={styles.FilterCheckbox}
          id="filterToggler"
          name="filterToggler"
          checked={isFilterCheckboxActive}
          onChange={handleFilterCheckboxChange}
        />
        <Button
          className={styles.FilterButton}
          text="Фильтр..."
          color="info"
          onClick={handleFilterButtonClick}
          {...filterButtonAttributes}
        />
      </div>
      {isFiltersModalVisible && (
        <DecisionListFiltersModal visible={isFiltersModalVisible} onClose={closeFiltersModal} />
      )}
      <ConfirmModal
        visible={isConfirmModalVisible}
        title="Подтверждение"
        text={`Вы выбрали ${keysCheckedItems.length} ${deletingQuantityText} для удаления. Удалить?`}
        applyButtonText="Удалить"
        cancelButtonText="Отменить"
        onApply={handleDeleteClick}
        onClose={handleDeleteModalClose}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionListPanelStoreProps => ({
  data: getDecisionListData(state),
  isSuperVisor: getUserRoleIsSupervisor(state),
  isTestUser: getUserProfileIsTest(state),
  decisionsCount: getDecisionListTotalCount(state),
});

export const DecisionListPanel = connect(mapStateToProps)(DecisionListPanelWithoutStore);
